// Async session for expo-sqlite, modeled on `drizzle-orm/op-sqlite/session.ts`.
//
// `drizzle-orm/expo-sqlite` ships a SYNC session built around `prepareSync` /
// `executeSync`, which deadlocks on web (wa-sqlite's worker can't service the
// main-thread spin wait). This module is the async counterpart — same shape as
// op-sqlite's session, but bound to expo-sqlite's async API
// (`prepareAsync` / `executeAsync` / `runAsync` / `getAllAsync` / `getFirstAsync`).
//
// Reference: https://raw.githubusercontent.com/drizzle-team/drizzle-orm/48e5406027103a9fca6eb66417187c4a8b5c6aa3/drizzle-orm/src/op-sqlite/session.ts

import type * as V1 from "@-/com.npmjs.drizzle-orm/_relations";
import {
  entityKind,
  fillPlaceholders,
  is,
  makeJitRqbMapper,
  NoopLogger,
  sql,
  type AnyRelations,
  type Logger,
  type Query,
  type RelationalQueryMapperConfig,
} from "@-/com.npmjs.drizzle-orm";
import { NoopCache, type Cache } from "drizzle-orm/cache/core";
import type { QueryMetadata, WithCacheConfig } from "drizzle-orm/cache/core/types";
// drizzle-orm 1.0.0-rc.4 SPLIT the sqlite-core session/db classes into an explicit `async/`
// namespace: `SQLiteSession`/`SQLiteTransaction`/`SQLitePreparedQuery`/`BaseSQLiteDatabase` became
// `SQLiteAsyncSession`/`SQLiteAsyncTransaction`/`SQLiteAsyncPreparedQuery`/`SQLiteAsyncDatabase`,
// while the two dialects collapsed into a single `SQLiteDialect`. The pre-rc.4 prerelease this
// wrapper was written against (`1.0.0-rc.4-5d5b77c`) still had the old names.
import {
  SQLiteAsyncPreparedQuery,
  SQLiteAsyncSession,
  SQLiteAsyncTransaction,
  type PreparedQueryConfig as PreparedQueryConfigBase,
  type SQLiteDialect,
  type SQLiteExecuteMethod,
  type SQLiteTransactionConfig,
  type SelectedFieldsOrdered,
} from "@-/com.npmjs.drizzle-orm/sqlite-core";
import type {
  DrizzleAsyncSQLiteBindValue,
  DrizzleAsyncSQLiteClient,
} from "#expo-sqlite-async/client";
import { isAnyObject } from "@-/com.npmjs.is-what";
import type * as SQLite from "@-/com.npmjs.expo-sqlite";
import {
  decrementSuppressionDepth,
  getDatabaseNameTag,
  incrementSuppressionDepth,
  takeCurrentCallsiteLogger,
} from "#expo-sqlite-async/logger-context";
import {
  formatQueryLine,
  getQueryType,
  parseSqliteExplain,
  shouldFormatQueryLines,
  shouldLogExecutionPlan,
  shouldLogQuery,
  type ExplainStats,
} from "#expo-sqlite-async/log-format";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";

function isSqliteBindValue(value: unknown): value is DrizzleAsyncSQLiteBindValue {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value instanceof Uint8Array
  );
}

function toSqliteBindValues(params: unknown[]): DrizzleAsyncSQLiteBindValue[] {
  const bindValues: DrizzleAsyncSQLiteBindValue[] = [];
  for (const param of params) {
    if (!isSqliteBindValue(param)) {
      throw new TypeError(`Invalid SQLite bind value: ${String(param)}`);
    }
    bindValues.push(param);
  }
  return bindValues;
}

function isSQLiteAsyncDialect(value: unknown): value is SQLiteDialect {
  return isAnyObject(value) && "sqlToQuery" in value;
}

interface PreparedQueryInternals {
  // rc.4 signature: (queryString, params, executeMethod, query).
  queryWithCache: <TResult>(
    queryString: string,
    params: unknown[],
    executeMethod: SQLiteExecuteMethod,
    query: () => Promise<TResult>,
  ) => Promise<TResult>;
}

function getPreparedQueryInternals(
  instance: SQLiteAsyncPreparedQuery<PreparedQueryConfigBase & { type: "async" }>,
): PreparedQueryInternals {
  const queryWithCache = Reflect.get(instance, "queryWithCache");
  if (typeof queryWithCache !== "function") {
    throw new TypeError("Prepared query is missing queryWithCache");
  }
  return {
    // rc.4 inserted an `executeMethod` argument:
    //   queryWithCache(queryString, params, executeMethod, query)
    // Passing the callback in the old third slot left `query` undefined, and the base then threw
    // `TypeError: query is not a function` on the first cached statement.
    queryWithCache: <TResult>(
      queryString: string,
      params: unknown[],
      executeMethod: SQLiteExecuteMethod,
      query: () => Promise<TResult>,
    ): Promise<TResult> =>
      Reflect.apply(queryWithCache, instance, [queryString, params, executeMethod, query]),
  };
}

function getSQLiteAsyncDialect(instance: object): SQLiteDialect {
  const dialect = Reflect.get(instance, "dialect");
  if (!isSQLiteAsyncDialect(dialect)) {
    throw new TypeError("Missing SQLite async dialect");
  }
  return dialect;
}

function isPreparedQueryResult<T>(value: unknown): value is T {
  return true;
}

function isRqbV2CustomResultMapper(
  mapper: unknown,
  isRqbV2: boolean | undefined,
): mapper is (rows: Record<string, unknown>[]) => unknown {
  return isRqbV2 === true && typeof mapper === "function";
}

function isExpoSQLiteAsyncSessionFor<
  TFullSchema extends Record<string, unknown>,
  TRelations extends AnyRelations,
  TSchema extends V1.TablesRelationalConfig,
>(value: unknown): value is ExpoSQLiteAsyncSession<TFullSchema, TRelations, TSchema> {
  return is(value, ExpoSQLiteAsyncSession);
}

function getRelationalQueryMapperConfig(
  config: RelationalQueryMapperConfig | undefined,
): RelationalQueryMapperConfig {
  if (config === undefined) {
    throw new TypeError("Expected relational query mapper config to be defined");
  }
  return config;
}

export interface ExpoSQLiteAsyncSessionOptions {
  cache?: Cache;
  logger?: Logger;
  // rc.5: `DrizzleQueryError` omits query params unless the database opts in.
  paramsInErrors?: boolean;
  useJitMappers?: boolean;
}

// `SQLiteRunResult` from expo-sqlite is `{ lastInsertRowId; changes }`.
export type ExpoSQLiteAsyncRunResult = SQLite.SQLiteRunResult;

type PreparedQueryConfig = Omit<PreparedQueryConfigBase, "statement" | "run">;

export class ExpoSQLiteAsyncSession<
  TFullSchema extends Record<string, unknown>,
  TRelations extends AnyRelations,
  TSchema extends V1.TablesRelationalConfig,
  // rc.4 signature is <TResultKind, TRunResult, TRelations>: the old `TFullSchema` and `TSchema`
  // generics are gone — relational typing now hangs off `TRelations` alone.
> extends SQLiteAsyncSession<"async", ExpoSQLiteAsyncRunResult, TRelations> {
  static override readonly [entityKind]: string = "ExpoSQLiteAsyncSession";

  private logger: Logger;
  private cache: Cache;

  constructor(
    private client: DrizzleAsyncSQLiteClient,
    dialect: SQLiteDialect,
    private relations: TRelations,
    private schema: V1.RelationalSchemaConfig<TSchema> | undefined,
    private options: ExpoSQLiteAsyncSessionOptions = {},
  ) {
    // rc.4 added the `resultKind` ctor argument (`SQLiteAsyncSession(dialect, resultKind)`); this
    // driver is always async.
    super(dialect, "async");
    this.logger = options.logger ?? new NoopLogger();
    this.cache = options.cache ?? new NoopCache();
  }

  // rc.4 RESHAPED this abstract method. It is now
  //   prepareQuery(query, mode, prepare, executeMethod?, mapper?, queryMetadata?, cacheConfig?)
  // where the old `fields: SelectedFieldsOrdered` slot became `mode: "arrays" | "objects" | "raw"`
  // plus a `prepare: boolean`, and the mapper takes `any[]`. `fields` is no longer threaded through
  // the session at all — the dialect owns field mapping — so it is not accepted here any more.
  override prepareQuery<T extends Omit<PreparedQueryConfig, "run">>(
    query: Query,
    _mode: "arrays" | "objects" | "raw",
    _prepare: boolean,
    executeMethod?: SQLiteExecuteMethod,
    // Must match rc.4's base signature exactly (`mapper?: (rows: any[]) => any`); narrowing to
    // `unknown` makes this override incompatible with `SQLiteAsyncSession.prepareQuery`.
    // oxlint-disable-next-line typescript/no-explicit-any
    customResultMapper?: (rows: any[]) => any,
    // rc.5: `tables` became a lazy `() => string[]`.
    queryMetadata?: QueryMetadata,
    cacheConfig?: WithCacheConfig,
  ): ExpoSQLiteAsyncPreparedQuery<T> {
    const prepared = new ExpoSQLiteAsyncPreparedQuery<T>(
      this.client,
      query,
      this.logger,
      this.cache,
      queryMetadata,
      cacheConfig,
      this.options.paramsInErrors,
      // rc.4 made `executeMethod` optional on `prepareQuery`; the prepared query still needs a
      // concrete method, and "all" is the base class's own default for an unspecified execute.
      executeMethod ?? "all",
      this.options.useJitMappers,
      customResultMapper,
    );
    // Read-and-clear the logger pushed synchronously by the query object's
    // `then`/`execute` immediately before this call (see logger-context).
    prepared.setCallsiteLogger(takeCurrentCallsiteLogger());
    return prepared;
  }

  // NOT `override`: rc.4's `SQLiteAsyncSession` no longer declares `prepareRelationalQuery`, so this
  // is now a wrapper-only method. Keeping the modifier made it a hard error; dropping it preserves
  // the method for the RQB-v2 callers inside this package that still invoke it directly.
  prepareRelationalQuery<T extends Omit<PreparedQueryConfig, "run">>(
    query: Query,
    fields: SelectedFieldsOrdered | undefined,
    executeMethod: SQLiteExecuteMethod,
    customResultMapper: (rows: Record<string, unknown>[]) => unknown,
    config: RelationalQueryMapperConfig,
  ): ExpoSQLiteAsyncPreparedQuery<T, true> {
    const prepared = new ExpoSQLiteAsyncPreparedQuery<T, true>(
      this.client,
      query,
      this.logger,
      this.cache,
      undefined,
      undefined,
      this.options.paramsInErrors,
      // rc.4 made `executeMethod` optional on `prepareQuery`; the prepared query still needs a
      // concrete method, and "all" is the base class's own default for an unspecified execute.
      executeMethod ?? "all",
      this.options.useJitMappers,
      customResultMapper,
      true,
      config,
    );
    prepared.setCallsiteLogger(takeCurrentCallsiteLogger());
    return prepared;
  }

  override async transaction<T>(
    transaction: (tx: ExpoSQLiteAsyncTransaction<TFullSchema, TRelations, TSchema>) => Promise<T>,
    config: SQLiteTransactionConfig = {},
  ): Promise<T> {
    if (this.client.withTransactionScope !== undefined) {
      return this.client.withTransactionScope((client) => {
        const owned = new ExpoSQLiteAsyncSession<TFullSchema, TRelations, TSchema>(
          client,
          getSQLiteAsyncDialect(this),
          this.relations,
          this.schema,
          this.options,
        );
        return owned.transaction(transaction, config);
      });
    }
    const dialect = getSQLiteAsyncDialect(this);
    const tx = new ExpoSQLiteAsyncTransaction<TFullSchema, TRelations, TSchema>(
      "async",
      dialect,
      this,
      // rc.4 dropped the trailing `schema` argument; the next positional slot is `nestedIndex`, so
      // passing `this.schema` here silently bound a RelationalSchemaConfig to a number parameter.
      Reflect.get(this, "relations") as TRelations,
    );
    await this.run(sql.raw(`begin${config.behavior ? " " + config.behavior : ""}`));
    try {
      const result = await transaction(tx);
      await this.run(sql`commit`);
      return result;
    } catch (error) {
      try {
        await this.run(sql`rollback`);
      } catch (rollbackError) {
        // Engines can abort the transaction themselves on a statement failure. Keep the
        // original cause (including SQLITE_BUSY) available to the caller's retry policy.
        // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError.errors retains rollbackError; cause preserves the original transaction failure for retry policy.
        throw new AggregateError(
          [error, rollbackError],
          `Transaction failed: ${error instanceof Error ? error.message : String(error)}; rollback also failed`,
          { cause: error },
        );
      }
      throw error;
    }
  }
}

export class ExpoSQLiteAsyncTransaction<
  TFullSchema extends Record<string, unknown>,
  TRelations extends AnyRelations,
  TSchema extends V1.TablesRelationalConfig,
  // Same rc.4 collapse as the session above.
> extends SQLiteAsyncTransaction<"async", ExpoSQLiteAsyncRunResult, TRelations> {
  static override readonly [entityKind]: string = "ExpoSQLiteAsyncTransaction";

  override async transaction<T>(
    transaction: (tx: ExpoSQLiteAsyncTransaction<TFullSchema, TRelations, TSchema>) => Promise<T>,
  ): Promise<T> {
    const dialect = getSQLiteAsyncDialect(this);
    const sessionValue = Reflect.get(this, "session");
    if (!isExpoSQLiteAsyncSessionFor<TFullSchema, TRelations, TSchema>(sessionValue)) {
      throw new TypeError("Expected ExpoSQLiteAsyncSession");
    }
    const savepointName = `sp${Reflect.get(this, "nestedIndex") as number}`;
    // rc.4 ctor: (resultType, dialect, session, relations, nestedIndex?, forbidJsonb?) — the
    // `schema` argument is gone. Relations live in the base database's `_` metadata;
    // there is no `relations` property on the transaction instance.
    const tx = new ExpoSQLiteAsyncTransaction<TFullSchema, TRelations, TSchema>(
      "async",
      dialect,
      sessionValue,
      this._.relations,
      (Reflect.get(this, "nestedIndex") as number) + 1,
    );
    await sessionValue.run(sql.raw(`savepoint ${savepointName}`));
    try {
      const result = await transaction(tx);
      await sessionValue.run(sql.raw(`release savepoint ${savepointName}`));
      return result;
    } catch (error) {
      try {
        await sessionValue.run(sql.raw(`rollback to savepoint ${savepointName}`));
      } catch (rollbackError) {
        // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError.errors retains rollbackError; cause preserves the original savepoint failure for retry policy.
        throw new AggregateError(
          [error, rollbackError],
          `Savepoint failed: ${error instanceof Error ? error.message : String(error)}; rollback also failed`,
          { cause: error },
        );
      }
      throw error;
    }
  }
}

export class ExpoSQLiteAsyncPreparedQuery<
  T extends PreparedQueryConfig = PreparedQueryConfig,
  TIsRqbV2 extends boolean = false,
> extends SQLiteAsyncPreparedQuery<{
  all: T["all"];
  execute: T["execute"];
  get: T["get"];
  run: ExpoSQLiteAsyncRunResult;
  type: "async";
  values: T["values"];
}> {
  static override readonly [entityKind]: string = "ExpoSQLiteAsyncPreparedQuery";

  private jitRqbMapper?: ReturnType<typeof makeJitRqbMapper<T["all"]>>;

  constructor(
    private client: DrizzleAsyncSQLiteClient,
    query: Query,
    // NOT `private`. rc.4's `SQLiteAsyncPreparedQuery` declares `logger` (and `cache`) as PROTECTED
    // members, so redeclaring them `private` here made this class structurally incompatible with its
    // own base — every `prepareQuery` return and every `this` passed to a base-typed parameter was
    // rejected with "Property 'logger' is private in type ... but not in type ...". Declaring it
    // `protected` matches the base and keeps it inaccessible from outside the hierarchy.
    protected override logger: Logger,
    cache: Cache,
    queryMetadata: QueryMetadata | undefined,
    cacheConfig: WithCacheConfig | undefined,
    // rc.5 dropped the per-driver `fields` + `joinsNotNullableMap` mapping (and `mapResultRow`):
    // the dialect builds every row mapper, which reaches this class as `customResultMapper`.
    paramsInErrors: boolean | undefined,
    executeMethod: SQLiteExecuteMethod,
    private useJitMappers: boolean | undefined,
    private customResultMapper?: (
      rows: TIsRqbV2 extends true ? Record<string, unknown>[] : unknown[][],
    ) => unknown,
    private isRqbV2Query?: TIsRqbV2,
    private rqbConfig?: RelationalQueryMapperConfig,
  ) {
    // rc.5 ctor:
    //   (resultKind, executeMethod, executors, query, mapper, mode, logger, cache, queryMetadata,
    //    cacheConfig, paramsInErrors)
    // The `executors` record (`all`/`get`/`run`; rc.5 serves `values` from `all`) is the base
    // class's own execution path. THIS class overrides `run`/`all`/`get`/`values` outright (they go
    // through `client` + the mappers below), so the base implementations are never reached — pass
    // throwing stubs rather than dead duplicates of the real logic, so any future call that DOES
    // fall through to the base fails loudly instead of silently doing nothing.
    super(
      "async",
      executeMethod,
      {
        all: () => {
          throw new Error("ExpoSQLiteAsyncPreparedQuery overrides all()");
        },
        get: () => {
          throw new Error("ExpoSQLiteAsyncPreparedQuery overrides get()");
        },
        run: () => {
          throw new Error("ExpoSQLiteAsyncPreparedQuery overrides run()");
        },
      },
      query,
      undefined,
      "objects",
      logger,
      cache,
      queryMetadata,
      cacheConfig,
      paramsInErrors,
    );
  }

  // Per-callsite logger threaded from `db(import.meta.location)`; set by the
  // session right after construction (see `setCallsiteLogger`).
  private callsiteLogger?: Console;

  setCallsiteLogger(logger: Console | undefined): void {
    this.callsiteLogger = logger;
  }

  // Run the actual SQLite round trip. When a per-callsite logger is threaded
  // through (via `db(import.meta.location)`), this times the call, suppresses the
  // raw-connection wrapper's own log for the duration, and emits a single
  // colored line prefixed with the ORIGINAL call site's `[file:line:col]`.
  private async runClient<TResult>(
    queryText: string,
    bindParams: readonly unknown[],
    op: () => Promise<TResult>,
  ): Promise<TResult> {
    const logger = this.callsiteLogger;
    if (logger === undefined || !shouldFormatQueryLines() || !shouldLogQuery(queryText)) {
      return await op();
    }

    incrementSuppressionDepth(this.client);
    const startTime = Date.now();
    try {
      return await op();
    } finally {
      // Keep suppression up through the EXPLAIN probe so the raw-connection
      // wrapper doesn't log it as a separate query.
      const duration = Date.now() - startTime;
      // Read the native-time accumulator surfaced by the patched expo-sqlite
      // (see patches/expo-sqlite@56.0.4.patch) BEFORE running the EXPLAIN
      // probe, which would overwrite it with EXPLAIN's own native time.
      const nativeMicrosRaw = Reflect.get(this.client as object, "__haniverseLastNativeMicros");
      const nativeMicros = typeof nativeMicrosRaw === "number" ? nativeMicrosRaw : null;
      let explainStats: ExplainStats | null = null;
      try {
        explainStats = await this.getExplainStats(queryText, bindParams);
      } finally {
        decrementSuppressionDepth(this.client);
      }
      logger.log(
        formatQueryLine(
          queryText,
          duration,
          explainStats,
          nativeMicros,
          getDatabaseNameTag(this.client as object),
          bindParams,
        ),
      );
    }
  }

  private async getExplainStats(
    queryText: string,
    bindParams: readonly unknown[],
  ): Promise<ExplainStats | null> {
    if (!shouldLogExecutionPlan() || getQueryType(queryText) !== "SELECT") {
      return null;
    }
    try {
      const rows = await this.client.getAllAsync(
        `EXPLAIN QUERY PLAN ${queryText}`,
        toSqliteBindValues([...bindParams]),
      );
      return Array.isArray(rows) && isNonEmpty(rows) ? parseSqliteExplain(rows) : null;
    } catch {
      return null;
    }
  }

  override async run(
    placeholderValues?: Record<string, unknown>,
  ): Promise<ExpoSQLiteAsyncRunResult> {
    const params = fillPlaceholders(this.query.params, placeholderValues ?? {});
    this.logger.logQuery(this.query.sql, params);
    const internals = getPreparedQueryInternals(this);
    return await internals.queryWithCache(this.query.sql, params, "run", async () => {
      return await this.runClient(this.query.sql, params, () =>
        this.client.runAsync(this.query.sql, toSqliteBindValues(params)),
      );
    });
  }

  override async all(placeholderValues?: Record<string, unknown>): Promise<T["all"]> {
    if (this.isRqbV2Query) {
      return this.allRqbV2(placeholderValues);
    }
    const { client, customResultMapper, logger, query } = this;
    if (!customResultMapper) {
      const internals = getPreparedQueryInternals(this);
      const params = fillPlaceholders(query.params, placeholderValues ?? {});
      logger.logQuery(query.sql, params);
      const result = await internals.queryWithCache<T["all"]>(
        query.sql,
        params,
        "all",
        async () => {
          return await this.runClient(query.sql, params, () =>
            client.getAllAsync(query.sql, toSqliteBindValues(params)),
          );
        },
      );
      if (isPreparedQueryResult<T["all"]>(result)) {
        return result;
      }
      throw new TypeError("Unexpected prepared query result");
    }
    const rows = await this.values(placeholderValues);
    if (!Array.isArray(rows)) {
      throw new TypeError("Expected values() to return row arrays");
    }
    const mapped = customResultMapper(rows);
    if (isPreparedQueryResult<T["all"]>(mapped)) {
      return mapped;
    }
    throw new TypeError("Unexpected custom result mapper output");
  }

  private async allRqbV2(placeholderValues?: Record<string, unknown>): Promise<T["all"]> {
    const { client, customResultMapper, logger, query } = this;
    const params = fillPlaceholders(query.params, placeholderValues ?? {});
    logger.logQuery(query.sql, params);
    const rows = await this.runClient(query.sql, params, () =>
      client.getAllAsync<Record<string, unknown>>(query.sql, toSqliteBindValues(params)),
    );
    const rqbConfig = getRelationalQueryMapperConfig(this.rqbConfig);
    if (this.useJitMappers) {
      const mapper = this.jitRqbMapper ?? makeJitRqbMapper<T["all"]>(rqbConfig);
      this.jitRqbMapper = mapper;
      const mapped = mapper(rows);
      if (isPreparedQueryResult<T["all"]>(mapped)) {
        return mapped;
      }
      throw new TypeError("Unexpected RQB JIT mapper output");
    }
    if (!isRqbV2CustomResultMapper(customResultMapper, this.isRqbV2Query)) {
      throw new TypeError("Expected custom result mapper for RQB v2 query");
    }
    const mapped = customResultMapper(rows);
    if (isPreparedQueryResult<T["all"]>(mapped)) {
      return mapped;
    }
    throw new TypeError("Unexpected RQB custom result mapper output");
  }

  override async get(placeholderValues?: Record<string, unknown>): Promise<T["get"]> {
    if (this.isRqbV2Query) {
      return this.getRqbV2(placeholderValues);
    }
    const { client, customResultMapper, logger, query } = this;
    const params = fillPlaceholders(query.params, placeholderValues ?? {});
    logger.logQuery(query.sql, params);
    if (!customResultMapper) {
      const internals = getPreparedQueryInternals(this);
      const row = await internals.queryWithCache<T["get"] | undefined>(
        query.sql,
        params,
        "get",
        async () => {
          return await this.runClient(query.sql, params, () =>
            client.getFirstAsync(query.sql, toSqliteBindValues(params)),
          );
        },
      );
      if (row === undefined || row === null) {
        return undefined;
      }
      if (isPreparedQueryResult<T["get"]>(row)) {
        return row;
      }
      throw new TypeError("Unexpected prepared query row");
    }
    const rows = await this.values(placeholderValues);
    if (!Array.isArray(rows)) {
      throw new TypeError("Expected values() to return row arrays");
    }
    const row = rows[0];
    if (!row) {
      return undefined;
    }
    // Same contract as the base `get`: the mapper maps a row list, and `get` takes the first.
    const mappedRows = customResultMapper([row]);
    if (!Array.isArray(mappedRows)) {
      throw new TypeError("Expected the custom result mapper to return an array");
    }
    const mapped: unknown = mappedRows[0];
    if (isPreparedQueryResult<T["get"]>(mapped)) {
      return mapped;
    }
    throw new TypeError("Unexpected custom result mapper output");
  }

  private async getRqbV2(placeholderValues?: Record<string, unknown>): Promise<T["get"]> {
    const { client, customResultMapper, logger, query } = this;
    const params = fillPlaceholders(query.params, placeholderValues ?? {});
    logger.logQuery(query.sql, params);
    const rows = await this.runClient(query.sql, params, () =>
      client.getAllAsync<Record<string, unknown>>(query.sql, toSqliteBindValues(params)),
    );
    const row = rows[0];
    if (!row) {
      return undefined;
    }
    const rqbConfig = getRelationalQueryMapperConfig(this.rqbConfig);
    if (this.useJitMappers) {
      const mapper = this.jitRqbMapper ?? makeJitRqbMapper<T["get"]>(rqbConfig);
      this.jitRqbMapper = mapper;
      const mapped = mapper(rows);
      if (isPreparedQueryResult<T["get"]>(mapped)) {
        return mapped;
      }
      throw new TypeError("Unexpected RQB JIT mapper output");
    }
    if (!isRqbV2CustomResultMapper(customResultMapper, this.isRqbV2Query)) {
      throw new TypeError("Expected custom result mapper for RQB v2 query");
    }
    const mapped = customResultMapper([row]);
    if (isPreparedQueryResult<T["get"]>(mapped)) {
      return mapped;
    }
    throw new TypeError("Unexpected RQB custom result mapper output");
  }

  override async values(placeholderValues?: Record<string, unknown>): Promise<T["values"]> {
    const params = fillPlaceholders(this.query.params, placeholderValues ?? {});
    this.logger.logQuery(this.query.sql, params);
    const internals = getPreparedQueryInternals(this);
    const result = await internals.queryWithCache<T["values"]>(
      this.query.sql,
      params,
      "values",
      async () => {
        return await this.runClient(this.query.sql, params, async () => {
          // expo-sqlite's SQLiteExecuteAsyncResult is an async iterable that fetches
          // one row per bridge call. For multi-row results that's O(N) round trips
          // (an 8k-row scan can take >10s on iOS even when SQLite itself runs in 50ms).
          // `getAllAsync()` pulls every row in a single bridge call instead.
          const statement = await this.client.prepareAsync(this.query.sql);
          try {
            const rawResult = await statement.executeForRawResultAsync<Record<string, unknown>>(
              toSqliteBindValues(params),
            );
            return await rawResult.getAllAsync();
          } finally {
            await statement.finalizeAsync();
          }
        });
      },
    );
    if (isPreparedQueryResult<T["values"]>(result)) {
      return result;
    }
    throw new TypeError("Unexpected prepared query values");
  }
}
